import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { TransportSide } from '@/new-transport/_models/transport.model';
import { TransportTimetable } from '@/new-transport/_models/transport-timetable.model';
import { PaginatedTimetableResponse, TermTimetableRow, TransportTermResponse } from './term-stop-sales-dialog.model';

@Injectable({
	providedIn: 'root',
})
export class TermStopSalesApiService {
	private readonly httpClient = inject(HttpClient);
	private readonly apiUrl = environment.apiUrl;

	getTermTimetables(termId: number): Observable<TermTimetableRow[]> {
		return this.httpClient.get<TransportTermResponse>(`${this.apiUrl}/transport/transport_term/${termId}/`).pipe(
			switchMap((term) => {
				const transportIds = [term.transportDeparture?.id, term.transportReturn?.id].filter(
					(id): id is number => typeof id === 'number',
				);
				if (transportIds.length === 0) {
					return of([] as TermTimetableRow[]);
				}
				return this.fetchTimetables(transportIds).pipe(map((rows) => this.toRows(rows, term)));
			}),
		);
	}

	patchTimetableStopSale(timetableId: number, stopSale: boolean): Observable<TransportTimetable> {
		return this.httpClient.patch<TransportTimetable>(`${this.apiUrl}/transport/timetable/${timetableId}/`, {
			stopSale,
		});
	}

	private fetchTimetables(transportIds: number[]): Observable<TransportTimetable[]> {
		const ids = transportIds.join(',');
		return this.httpClient
			.get<
				PaginatedTimetableResponse | TransportTimetable[]
			>(`${this.apiUrl}/transport/timetable/?transport=${ids}&limit=999`)
			.pipe(map((response) => (Array.isArray(response) ? response : (response.results ?? []))));
	}

	private toRows(rows: TransportTimetable[], term: TransportTermResponse): TermTimetableRow[] {
		const transportReturnId = term.transportReturn?.id ?? null;
		return rows
			.map((row) => this.toRow(row, transportReturnId))
			.sort((a, b) => {
				if (a.side !== b.side) {
					return a.side === TransportSide.DEPARTURE ? -1 : 1;
				}
				const aTime = a.timeDeparture ?? '';
				const bTime = b.timeDeparture ?? '';
				return aTime.localeCompare(bTime);
			});
	}

	private toRow(row: TransportTimetable, transportReturnId: number | null): TermTimetableRow {
		return {
			id: row.id,
			stopId: row.stop.id,
			stopName: row.stop.location?.name ?? '',
			timeDeparture: row.timeDeparture ?? null,
			side: this.resolveSide(row.transport, transportReturnId),
			stopSale: row.stopSale ?? false,
		};
	}

	private resolveSide(transportId: number, transportReturnId: number | null): TransportSide {
		return transportReturnId !== null && transportId === transportReturnId
			? TransportSide.RETURN
			: TransportSide.DEPARTURE;
	}
}
